import { ReactNode } from 'react'
import { colors, Chip } from '@resiliatech/birdsong'
import CachedOutlinedIcon from '@mui/icons-material/CachedOutlined'
import ContentCopyIcon from '@mui/icons-material/ContentCopyOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import DrawIcon from '@mui/icons-material/DrawOutlined'
import FeedOutlinedIcon from '@mui/icons-material/FeedOutlined'
import SchoolIcon from '@mui/icons-material/SchoolOutlined'
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined'
import QuestionMarkOutlinedIcon from '@mui/icons-material/QuestionMarkOutlined'
import MissedVideoCallIcon from '@mui/icons-material/MissedVideoCall'
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined'
import ThreePOutlinedIcon from '@mui/icons-material/ThreePOutlined'
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined'
import {
  ModuleContent,
  Resource,
  ResourceType,
  Course,
} from 'Generated/academy-graphql-types'
import { stringToTitleCase } from './utils'

export type ValidChipTypes =
  | ResourceType
  | 'help'
  | 'post'
  | 'event'
  | 'comment'
  | 'recording'

type ResourceChipProps = {
  resource: ModuleContent | Resource | { type: ValidChipTypes }
}

const CHIP_ICON_SIZE = 'small'

export const ResourceChip = ({ resource }: ResourceChipProps) => {
  if (!resource || !resource.type) return null

  const defaultIcon = <FeedOutlinedIcon fontSize={CHIP_ICON_SIZE} />

  const resourceMap: Record<
    ValidChipTypes,
    {
      text:
        | 'Activity'
        | 'Article'
        | 'Course'
        | 'Track'
        | 'Template'
        | 'Help'
        | 'Post'
        | 'Comment'
        | 'Event'
        | 'Recording'
      icon?: ReactNode
      iconBgColor?: string
    }
  > = {
    activity: {
      text: 'Activity',
      icon: <DrawIcon fontSize={CHIP_ICON_SIZE} />,
      iconBgColor: colors.visualization.tintMagenta,
    },
    article: {
      text: 'Article',
      icon: <FeedOutlinedIcon fontSize={CHIP_ICON_SIZE} />,
      iconBgColor: colors.visualization.tintGreen,
    },
    course: {
      text: 'Course',
      icon: <SchoolIcon fontSize={CHIP_ICON_SIZE} />,
      iconBgColor: colors.visualization.tintBrown,
    },
    learning_track: {
      text: 'Track',
      icon: defaultIcon,
      iconBgColor: colors.visualization.tintIndigo,
    },
    template: {
      text: 'Template',
      icon: <ContentCopyIcon fontSize={CHIP_ICON_SIZE} />,
      iconBgColor: colors.visualization.tintCobalt,
    },
    help: {
      text: 'Help',
      icon: <QuestionMarkOutlinedIcon fontSize={CHIP_ICON_SIZE} />,
      iconBgColor: colors.background.tertiaryBackground,
    },
    event: {
      text: 'Event',
      icon: <EventAvailableOutlinedIcon fontSize={CHIP_ICON_SIZE} />,
      iconBgColor: colors.visualization.tintForestGreen,
    },
    post: {
      text: 'Post',
      icon: <ThreePOutlinedIcon fontSize={CHIP_ICON_SIZE} />,
      iconBgColor: colors.visualization.tintBurgundy,
    },
    comment: {
      text: 'Comment',
      icon: <NotesOutlinedIcon fontSize={CHIP_ICON_SIZE} />,
      iconBgColor: colors.visualization.tintBerry,
    },
    recording: {
      text: 'Recording',
      icon: <MissedVideoCallIcon fontSize={CHIP_ICON_SIZE} />,
      iconBgColor: colors.visualization.tintTeal,
    },
  }

  const resourceType = resource.type
  const { iconBgColor } = resourceMap[resourceType]

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const ResourceIcon = resourceMap[resourceType || ResourceType.Article].icon

  return (
    <Chip
      icon={ResourceIcon}
      text={resourceMap[resource.type].text}
      backgroundColor={iconBgColor}
    />
  )
}

export const CompletedChip = ({ text = 'Completed' }: { text?: string }) => {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const CompletedIcon = (
    <TaskAltOutlinedIcon
      sx={{ color: colors.feedback.successDark }}
      fontSize={CHIP_ICON_SIZE}
    />
  )
  return <Chip icon={CompletedIcon} text={text} variant='success' />
}

CompletedChip.defaultProps = {
  text: 'Completed',
}

export const GatedChip = () => {
  const gatedIcon = <LockOutlinedIcon fontSize={CHIP_ICON_SIZE} />
  return <Chip icon={gatedIcon} text='Upgrade Required' variant='warning' />
}

GatedChip.defaultProps = {
  format: undefined,
}

export const InProgressChip = () => {
  const inProgressIcon = <CachedOutlinedIcon fontSize={CHIP_ICON_SIZE} />
  return <Chip icon={inProgressIcon} text='In Progress' variant='warning' />
}

export const StatusChip = ({ resource }: { resource: Resource }) => {
  if (resource.type === 'course') {
    if ((resource as Course).status === 'completed') {
      return CompletedChip({ text: 'Completed' })
    }
    if ((resource as Course).status === 'in_progress') {
      return InProgressChip()
    }
  }
  return null
}
